import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Check, MessageCircle, XCircle, Mail, Eye, Search, FileText, LinkIcon, Copy } from 'lucide-react';
import PopupComponent from '../../components/PopupComponent';
import ContactLinksGenerator from '../../utils/contactlinksgenerator';
import logger, { Logger } from '../../utils/logger';
import { shareContentLead } from '../../services/leadService';
import { sendTestEmail, sendEmailByBrevo, sendEmailByZeptomail } from '../../services/emailService';
import { sendMessageToLead } from '../../services/whatsapp';
import { getTemplates } from '../../services/templates';
import { useAppSelector } from '../../hooks/hooks';
import { selectWhatsappPhoneId, selectIsWhatsappIntegrated, selectWhatsappIntegration } from '../../store/slices/userSlice';
import type { RootState } from '../../store/store';
import styles from './BulkShareModal.module.scss';
import styled from 'styled-components';

// Define the interfaces
interface ContentTemplate {
  _id: string;
  type: string;
  name?: string;
  details?: {
    title?: string;
    message?: string;
    description?: string;
    subject?: string;
    type?: string;
  };
  files?: Array<{
    fileName: string;
  }>;
}

type ContentType = 'whatsapp_api' | 'email' | 'message' | 'file' | 'page';

interface Lead {
  _id: string;
  name?: string;
  phone?: string;
  email?: string;
}

interface SenderDetail {
  _id?: string;
  email: string;
  name: string;
  service: 'gmail' | 'brevo' | 'zeptomail';
}

interface BulkShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLeads: Lead[];
  contentTemplates: ContentTemplate[];
}

// ============================================
// STYLED COMPONENTS
// ============================================

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const Chip = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 2px solid ${props => props.active ? '#3faefd' : '#e5e7eb'};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active ? '#dbeafe' : 'white'};
  color: ${props => props.active ? '#0284c7' : '#6b7280'};

  &:hover {
    border-color: #3faefd;
    background: ${props => props.active ? '#dbeafe' : '#f0f9ff'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
  }
`;

const SearchInputContainer = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3faefd;
    box-shadow: 0 0 0 2px rgba(63, 174, 253, 0.1);
  }

  &:hover {
    border-color: #d1d5db;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  display: flex;
  align-items: center;
  pointer-events: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TemplateDropdown = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  max-height: 250px;
  overflow-y: auto;
`;

const TemplateOption = styled.div<{ selected: boolean }>`
  padding: 10px 12px;
  cursor: pointer;
  background: ${props => props.selected ? '#dbeafe' : 'white'};
  border-left: 3px solid ${props => props.selected ? '#3faefd' : 'transparent'};
  transition: all 0.2s ease;
  font-size: 13px;
  color: #374151;
  position: relative;

  &:hover {
    background: #f0f9ff;
  }

  .template-title {
    font-weight: 500;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .template-preview {
    font-size: 12px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.4;
  }

  &:hover .template-tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const TemplateTooltip = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background: #1f2937;
  color: white;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
  margin-bottom: 4px;
  white-space: normal;
  word-wrap: break-word;
  max-width: 300px;
  line-height: 1.4;
  transition: all 0.2s ease;
`;

const PreviewBox = styled.div`
  background: #f8fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
  max-height: 180px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
`;


const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const StatCard = styled.div`
  background: #f8fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  text-align: center;

  .stat-label {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 4px;
  }

  .stat-value {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }
`;

const LeadItemContainer = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  &.completed {
    background: #f0fdf4;
    border-color: #86efac;
  }

  &.error {
    background: #fef2f2;
    border-color: #fca5a5;
  }

  &:hover {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const LeadInfo = styled.div`
  flex: 1;

  .lead-name {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
  }

  .lead-contact {
    font-size: 12px;
    color: #6b7280;
  }
`;

const StatusBadge = styled.div<{ status: 'pending' | 'loading' | 'completed' | 'error' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;

  ${props => {
    switch (props.status) {
      case 'completed':
        return 'background: #d1fae5; color: #059669;';
      case 'loading':
        return 'background: #dbeafe; color: #0284c7;';
      case 'error':
        return 'background: #fee2e2; color: #dc2626;';
      default:
        return 'background: #f3f4f6; color: #6b7280;';
    }
  }}
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button<{ primary?: boolean; danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    if (props.danger) {
      return `
        background: #fee2e2;
        color: #dc2626;
        &:hover:not(:disabled) {
          background: #fecaca;
        }
      `;
    }
    if (props.primary) {
      return `
        background: #3faefd;
        color: white;
        &:hover:not(:disabled) {
          background: #2196f3;
        }
      `;
    }
    return `
      background: #e5e7eb;
      color: #374151;
      &:hover:not(:disabled) {
        background: #d1d5db;
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BulkShareModal: React.FC<BulkShareModalProps> = ({
  isOpen,
  onClose,
  selectedLeads,
  contentTemplates,
}) => {
  // ============================================
  // REDUX SELECTORS
  // ============================================
  const phoneId = useAppSelector(selectWhatsappPhoneId);
  const isWhatsappIntegrated = useAppSelector(selectIsWhatsappIntegrated);
  const whatsappIntegration = useAppSelector(selectWhatsappIntegration);
  const userDetails = useAppSelector((state: RootState) => state.rootReducers?.userData?.userDetails);

  // ============================================
  // WHATSAPP INTEGRATION VALIDATION
  // ============================================
  // 🔥 DEBUG: Check BOTH old and new Redux structures
  Logger.log('🔍 DEBUG: Checking WhatsApp integration from multiple sources:', {
    // New Redux Toolkit slice
    newSlice_isWhatsappIntegrated: isWhatsappIntegrated,
    newSlice_phoneId: phoneId || '(missing)',
    newSlice_whatsappIntegration: whatsappIntegration,
    // Old Redux structure
    oldRedux_userDetails: userDetails,
    oldRedux_userIntegrations: Array.isArray(userDetails)
      ? userDetails[0]?.userIntegrations
      : (userDetails as any)?.userIntegrations
  });

  // 🔥 Try to get WhatsApp phoneId from OLD Redux structure as fallback
  const userData = Array.isArray(userDetails)
    ? (userDetails.length > 0 ? userDetails[0] : null)
    : userDetails;

  const oldReduxWhatsappIntegration = userData?.userIntegrations?.find(
    (item: any) => item?.integration?.key === "whatsapp_business" && item?.isActive
  );

  const oldReduxPhoneId = oldReduxWhatsappIntegration?.details?.[0]?.phoneNumberId;

  Logger.log('🔍 DEBUG: Old Redux WhatsApp integration:', {
    found: Boolean(oldReduxWhatsappIntegration),
    phoneId: oldReduxPhoneId || '(missing)',
    integration: oldReduxWhatsappIntegration
  });

  // Use whichever source has valid data
  const actualPhoneId = phoneId || oldReduxPhoneId;
  const actualIsIntegrated = isWhatsappIntegrated || Boolean(oldReduxWhatsappIntegration);
  const hasValidWhatsAppAPI = actualIsIntegrated && Boolean(actualPhoneId?.trim());

  Logger.log('✅ FINAL WhatsApp API Integration Status:', {
    actualIsIntegrated,
    actualPhoneId: actualPhoneId || '(missing)',
    hasValidWhatsAppAPI,
    source: phoneId ? 'new Redux slice' : oldReduxPhoneId ? 'old Redux structure' : 'not found'
  });

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [selectedContentType, setSelectedContentType] = useState<ContentType>('message');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templateSearchQuery, setTemplateSearchQuery] = useState<string>('');
  const [completedLeads, setCompletedLeads] = useState<Record<string, boolean | 'loading' | 'error'>>({});
  const [templatePreview, setTemplatePreview] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState<any[]>([]);
  const [loadingWaTemplates, setLoadingWaTemplates] = useState(false);
  const [emailSenders, setEmailSenders] = useState<SenderDetail[]>([]);
  const [selectedSender, setSelectedSender] = useState<SenderDetail | null>(null);
  const [showSenderDropdown, setShowSenderDropdown] = useState(false);
  const [copiedLeadId, setCopiedLeadId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const whatsappWindowRef = useRef<Window | null>(null);
  const senderDropdownRef = useRef<HTMLDivElement>(null);

  // 🔥 Clear completedLeads when modal closes (reset for next time)
  useEffect(() => {
    if (!isOpen) {
      Logger.log('Modal closed - resetting completedLeads state');
      setCompletedLeads({});
      setShowSenderDropdown(false);
    }
  }, [isOpen]);

  // 🔥 Close sender dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (senderDropdownRef.current && !senderDropdownRef.current.contains(event.target as Node)) {
        setShowSenderDropdown(false);
      }
    };

    if (showSenderDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSenderDropdown]);

  // 🔥 Clear completedLeads when switching content types (allow multi-channel sending)
  useEffect(() => {
    Logger.log('🔄 Content type changed to:', selectedContentType, '- clearing completedLeads');
    Logger.log('Previous completedLeads state:', completedLeads);
    setCompletedLeads({});
    Logger.log('✅ completedLeads cleared - all buttons should show "Send" now');
  }, [selectedContentType]);

  // 🔥 Clear completedLeads when switching templates (allow multiple sends with different templates)
  useEffect(() => {
    if (selectedTemplate) {
      Logger.log('Template changed to:', selectedTemplate, '- clearing completedLeads to allow re-sending');
      setCompletedLeads({});
    }
  }, [selectedTemplate]);

  // Auto-switch logic for invalid configurations
  useEffect(() => {
    // Switch away from WhatsApp API if multiple leads selected
    if (selectedLeads.length > 1 && (selectedContentType === 'whatsapp_api' || selectedContentType === 'email')) {
      Logger.log('Multiple leads detected, switching from', selectedContentType, 'to message');
      setSelectedContentType('message');
      setSelectedTemplate('');
      setSelectedSender(null);
    }

    // 🔥 Switch away from WhatsApp API if integration is not valid
    if (selectedContentType === 'whatsapp_api' && !hasValidWhatsAppAPI) {
      Logger.log('WhatsApp API integration invalid, switching to message', {
        actualIsIntegrated,
        hasPhoneId: Boolean(actualPhoneId)
      });
      setSelectedContentType('message');
      setSelectedTemplate('');
      setSelectedSender(null);
    }
  }, [selectedLeads.length, selectedContentType, hasValidWhatsAppAPI, actualIsIntegrated, actualPhoneId]);

  // Fetch email senders from Redux when modal opens
  useEffect(() => {
    if (!isOpen) return;

    try {
      Logger.log('📧 [BulkShareModal] Loading email senders...');

      // 🔥 Read from localStorage like createWhatsappBroadcast does (more reliable)
      const userDataString = localStorage.getItem("userData");
      Logger.log('📧 [BulkShareModal] localStorage userData exists:', Boolean(userDataString));

      if (userDataString) {
        const userData = JSON.parse(userDataString);
        Logger.log('📧 [BulkShareModal] Parsed userData from localStorage:', userData);

        // Get existing mail senders from preferences for gmail and brevo
        const mailSender = userData?.userPreference?.mailSender || { gmail: [], brevo: [] };
        Logger.log('📧 [BulkShareModal] mailSender config:', mailSender);

        const allSenders: SenderDetail[] = [
          ...(mailSender.gmail || []).map((s: any) => ({ ...s, service: 'gmail' as const })),
          ...(mailSender.brevo || []).map((s: any) => ({ ...s, service: 'brevo' as const }))
        ];

        Logger.log('📧 [BulkShareModal] Gmail + Brevo senders:', allSenders);

        // 🔥 Check for ZeptoMail integration in userIntegrations (same as createWhatsappBroadcast)
        const userIntegrations = userData?.userIntegrations || [];
        Logger.log('📧 [BulkShareModal] userIntegrations count:', userIntegrations.length);

        // Log all integration keys for debugging
        const integrationKeys = userIntegrations.map((int: any) => ({
          key: int.integration?.key,
          isActive: int.isActive,
          isDeleted: int.isDeleted,
          hasDetails: Boolean(int.details),
          hasSenders: Boolean(int.details?.senders),
          sendersCount: int.details?.senders?.length || 0
        }));
        Logger.log('📧 [BulkShareModal] All integrations:', integrationKeys);

        // Find ZeptoMail integration (matching createWhatsappBroadcast exactly)
        const zeptoMailIntegration = userIntegrations.find((int: any) =>
          int.integration?.key === 'zeptomail' &&
          int.isActive === true &&
          int.isDeleted === false
        );

        Logger.log('📧 [BulkShareModal] ZeptoMail integration found:', Boolean(zeptoMailIntegration));

        // If ZeptoMail integration exists, add ALL senders to the list
        if (zeptoMailIntegration) {
          Logger.log('📧 [BulkShareModal] ZeptoMail integration:', zeptoMailIntegration);

          const senders = zeptoMailIntegration.details?.senders || [];
          Logger.log('📧 [BulkShareModal] ZeptoMail senders:', senders);

          // Map ALL ZeptoMail senders (like automationRuleDrawer does)
          const zeptoSenders = senders.map((sender: any) => ({
            _id: zeptoMailIntegration._id,
            email: sender.email,
            name: sender.name,
            service: 'zeptomail' as const
          }));

          Logger.log('📧 [BulkShareModal] Adding ZeptoMail senders:', zeptoSenders);
          allSenders.push(...zeptoSenders);
        } else {
          Logger.log('📧 [BulkShareModal] No active ZeptoMail integration found');

          // Debug: check if there's any zeptomail integration at all
          const anyZepto = userIntegrations.find((int: any) => int.integration?.key === 'zeptomail');
          if (anyZepto) {
            Logger.log('📧 [BulkShareModal] Found ZeptoMail but not active/deleted:', {
              isActive: anyZepto.isActive,
              isDeleted: anyZepto.isDeleted,
              details: anyZepto.details
            });
          }
        }

        setEmailSenders(allSenders);
        Logger.log('📧 [BulkShareModal] FINAL emailSenders:', allSenders);
      } else {
        Logger.log('📧 [BulkShareModal] No userData in localStorage');
      }
    } catch (error) {
      console.error('📧 [BulkShareModal] Error loading email senders:', error);
    }
  }, [isOpen]);

  // Fetch WhatsApp templates when modal opens
  useEffect(() => {
    const fetchWhatsAppTemplates = async () => {
      if (!isOpen) return;

      setLoadingWaTemplates(true);
      try {
        const response = await getTemplates({ page: 1, perPage: 100 });
        if (response?.data?.data) {
          // Filter only APPROVED templates
          const approvedTemplates = response.data.data
            .filter((t: any) => t.status === 'APPROVED')
            .map((t: any) => ({
              ...t,
              type: 'whatsapp_template'  // Mark as WhatsApp template
            }));
          setWhatsappTemplates(approvedTemplates);
          Logger.log('WhatsApp templates fetched:', approvedTemplates.length);
        }
      } catch (error) {
        console.error('Error fetching WhatsApp templates:', error);
      } finally {
        setLoadingWaTemplates(false);
      }
    };

    fetchWhatsAppTemplates();
  }, [isOpen]);

  // ============================================
  // VALIDATION & FILTERING
  // ============================================

  // Get appropriate leads based on selected content type
  const validLeads = useMemo(() => {

    return selectedLeads.filter(lead => {
      if (selectedContentType === 'whatsapp_api') {
        if (!lead.phone) return false;
        return /^[+]?[\d\s()-]{8,}$/.test(lead.phone.trim());
      } else if (selectedContentType === 'email') {
        if (!lead.email) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim());
      }
      // For message, file, page - no validation needed (any lead works)
      return true;
    });
  }, [selectedLeads, selectedContentType]);

  // Filter templates based on selected content type
  const filteredTemplates = useMemo(() => {
    if (selectedContentType === 'whatsapp_api') {
      // WhatsApp API uses separately fetched WhatsApp templates
      return whatsappTemplates;
    }

    // 🔥 Map UI content type to database template type
    let typeToMatch: string = selectedContentType;
    if (selectedContentType === 'email') {
      typeToMatch = 'email_template'; // Email uses email_template in database
    }

    Logger.log('📋 Filtering templates:', {
      selectedContentType,
      typeToMatch,
      availableTypes: [...new Set(contentTemplates.map(t => t.type))],
      filteredCount: contentTemplates.filter(template => template.type === typeToMatch).length
    });

    return contentTemplates.filter(template => template.type === typeToMatch);
  }, [selectedContentType, contentTemplates, whatsappTemplates]);

  // Search through templates
  const searchedTemplates = useMemo(() => {
    if (!templateSearchQuery.trim()) return filteredTemplates;

    const query = templateSearchQuery.toLowerCase();
    return filteredTemplates.filter(template =>
      (template.details?.title || '').toLowerCase().includes(query) ||
      (template.name || '').toLowerCase().includes(query)
    );
  }, [filteredTemplates, templateSearchQuery]);

  const totalLeads = validLeads.length;
  const completedCount = Object.keys(completedLeads).length;

  // Update template preview when selected
  useEffect(() => {
    if (selectedTemplate && selectedContentType) {
      // Find template from appropriate source
      let template;
      if (selectedContentType === 'whatsapp_api') {
        template = whatsappTemplates.find(t => t._id === selectedTemplate);
      } else {
        template = contentTemplates.find(t => t._id === selectedTemplate);
      }

      if (template) {
        let preview = '';

        if (selectedContentType === 'whatsapp_api') {
          // WhatsApp template structure
          const bodyComponent = template.components?.find((c: any) => c.type === 'BODY');
          preview = `📋 WhatsApp Template: ${template.name}\n\n${bodyComponent?.text || 'No body content'}`;
        } else if (template.type === 'file' || template.type === 'page') {
          const title = template.details?.title || 'Untitled';
          const fileInfo = template.type === 'file' && template.files && template.files.length > 0
            ? `File: ${template.files[0].fileName}`
            : '';

          preview = `Type: ${template.type.toUpperCase()}\n` +
            `Title: ${title}\n` +
            (fileInfo ? `${fileInfo}\n\n` : '\n') +
            `This ${template.type} will be shared with a unique link generated for each lead.`;
        } else {
          let content = '';

          // 🔥 IMPROVED: Handle email templates specifically
          if (template.type === 'email_template' || selectedContentType === 'email') {
            const subject = template.details?.subject || template.details?.title || 'No subject';
            const message = template.details?.message || '';

            // Show subject and preview of message (strip HTML for preview)
            const messagePreview = message.replace(/<[^>]*>/g, '').substring(0, 200);
            content = `📧 Subject: ${subject}\n\n${messagePreview}...`;

            Logger.log('📧 Email template preview generated:', {
              subject,
              messageLength: message.length,
              isHtml: template.details?.isHtmlTemplate
            });
          } else if (template.details?.message) {
            content = template.details.message;
          } else if (template.details?.subject) {
            content = `Subject: ${template.details.subject}\n\n${template.details.message || ''}`;
          } else if (template.details?.description) {
            content = template.details.description;
          }

          content = content.replace(/@lead name/g, 'Example Lead');
          preview = content || 'No content available for this template';
        }

        setTemplatePreview(preview);
      }
    } else {
      setTemplatePreview('');
    }
  }, [selectedTemplate, selectedContentType, contentTemplates, whatsappTemplates]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  // Helper function to generate message content for a lead (used by both Send and Copy)
  const generateMessageContent = useCallback((lead: Lead): string => {
    if (!selectedTemplate) return '';

    // Get the selected template
    const template = selectedContentType === 'whatsapp_api'
      ? whatsappTemplates.find(t => t._id === selectedTemplate)
      : contentTemplates.find(t => t._id === selectedTemplate);

    if (!template) return '';

    let content = '';

    if (selectedContentType === 'file' || selectedContentType === 'page') {
      // For file/page, generate a preview message (link will be added on send)
      content = `Hi ${lead.name || 'Customer'},\n\nHere's the link to view:\n[Link will be generated on send]`;
    } else {
      // For message/email/whatsapp_api, use template content
      if (template.details?.message) {
        content = template.details.message;
      } else if (template.details?.description) {
        content = template.details.description;
      } else if (selectedContentType === 'whatsapp_api') {
        // WhatsApp template body
        const bodyComponent = template.components?.find((c: any) => c.type === 'BODY');
        content = bodyComponent?.text || '';
      }

      // Personalize with lead name
      content = content.replace(/@lead name/g, lead.name || 'Customer');
    }

    return content;
  }, [selectedTemplate, selectedContentType, contentTemplates, whatsappTemplates]);

  // Copy message to clipboard
  const handleCopyMessage = useCallback(async (lead: Lead) => {
    const message = generateMessageContent(lead);
    if (!message) {
      alert('Please select a template first');
      return;
    }

    try {
      await navigator.clipboard.writeText(message);
      setCopiedLeadId(lead._id);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedLeadId(prev => prev === lead._id ? null : prev);
      }, 2000);

      Logger.log(`Message copied for ${lead.name}`);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = message;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLeadId(lead._id);
      setTimeout(() => {
        setCopiedLeadId(prev => prev === lead._id ? null : prev);
      }, 2000);
    }
  }, [generateMessageContent]);

  const handleSendToLead = async (lead: Lead) => {
    if (!selectedTemplate) {
      alert('Please select a template first');
      return;
    }

    try {
      const leadId = lead._id;
      setCompletedLeads(prev => ({
        ...prev,
        [leadId]: 'loading'
      }));

      Logger.log(`Sending ${selectedContentType} to ${lead.name}`);

      // Get the selected template from appropriate source
      let template;
      if (selectedContentType === 'whatsapp_api') {
        template = whatsappTemplates.find(t => t._id === selectedTemplate);
      } else {
        template = contentTemplates.find(t => t._id === selectedTemplate);
      }

      if (!template) {
        throw new Error(`Template not found (looking in ${selectedContentType === 'whatsapp_api' ? 'whatsappTemplates' : 'contentTemplates'})`);
      }

      // Compose message based on content type
      let messageContent = '';

      // Special handling for file and page content types
      if (selectedContentType === 'file' || selectedContentType === 'page') {
        try {
          Logger.log(`Processing ${selectedContentType} content for lead ${lead.name}`);

          // Call the shareContentLead API to generate a unique link
          const body = {
            contentId: template._id,
            leadIds: [lead._id],
            performedAt: new Date(),
            contentType: selectedContentType,
          };

          const response = await shareContentLead(body);

          if (!response?.data?.data?.uniqueLink) {
            throw new Error('No unique link generated');
          }

          Logger.log(`Generated unique link for ${selectedContentType}`);

          // Create message with the unique link
          messageContent = `Hi ${lead.name || "Customer"},

Here's the link to view:
${response.data.data.uniqueLink}`;
        } catch (error) {
          console.error('Error generating share link:', error);
          alert('Failed to generate share link');

          // Reset completed state on error
          setCompletedLeads(prev => {
            const newState = {...prev};
            delete newState[leadId];
            return newState;
          });

          return;
        }
      } else if (selectedContentType === 'message' || selectedContentType === 'whatsapp_api' || selectedContentType === 'email') {
        // Message/WhatsApp/Email: Use template content and personalize
        let content = '';

        if (selectedContentType === 'email') {
          // 🔥 For email: Check multiple possible fields where body content might be stored
          // Priority: html > message > description (html is the primary field for email templates)
          if (template.details?.html && template.details.html.trim().length > 0) {
            content = template.details.html;
          } else if (template.details?.message && template.details.message.trim().length > 0) {
            content = template.details.message;
          } else if (template.details?.description && template.details.description.trim().length > 0) {
            content = template.details.description;
          }

          Logger.log('📧 Email content extraction:', {
            hasHtml: Boolean(template.details?.html),
            htmlLength: template.details?.html?.length || 0,
            hasMessage: Boolean(template.details?.message),
            messageLength: template.details?.message?.length || 0,
            hasDescription: Boolean(template.details?.description),
            usedField: template.details?.html ? 'html' : template.details?.message ? 'message' : 'description',
            contentLength: content.length
          });
        } else {
          // For message/whatsapp_api: Original logic
          if (template.details?.message) {
            content = template.details.message;
          } else if (template.details?.description) {
            content = template.details.description;
          }
        }

        // Personalize with lead name - handle multiple placeholder formats
        messageContent = content
          .replace(/@lead name/gi, lead.name || 'Customer')
          .replace(/\{\{Name\}\}/gi, lead.name || 'Customer')
          .replace(/\{\{name\}\}/gi, lead.name || 'Customer')
          .replace(/\{\{lead\.name\}\}/gi, lead.name || 'Customer');
      }

      Logger.log(`Message composed: ${messageContent.substring(0, 50)}...`);

      // Open WhatsApp/Email or share based on content type
      if ((selectedContentType === 'message' || selectedContentType === 'file' || selectedContentType === 'page') && lead.phone) {
        // Open WhatsApp with message using NAMED WINDOW to reuse same tab
        const contactGenerator = new ContactLinksGenerator({
          number: lead.phone,
          message: messageContent
        });

        // Use named window 'whatsapp-share' to reuse the same tab instead of opening multiple tabs
        // This works with both WhatsApp Web AND WhatsApp Desktop:
        // - If Desktop app is installed, OS will prompt user to choose (Web vs Desktop)
        // - If user chooses Web, subsequent sends will reuse the same browser tab
        // - If user chooses Desktop, the app will handle each message
        const whatsappUrl = contactGenerator.whatsapp;

        // Try to reuse existing window reference, or open with named window
        if (whatsappWindowRef.current && !whatsappWindowRef.current.closed) {
          // Window exists and is still open - navigate it to new URL
          whatsappWindowRef.current.location.href = whatsappUrl;
          whatsappWindowRef.current.focus();
        } else {
          // Open new window with a name so it can be reused
          whatsappWindowRef.current = window.open(whatsappUrl, 'whatsapp-share');
        }
      } else if (selectedContentType === 'whatsapp_api' && lead.phone) {
        // Send via WhatsApp Business API (requires approved template)
        try {
          // 🔥 CRITICAL: Validate phoneId before sending
          if (!actualPhoneId || !actualPhoneId.trim()) {
            const errorMsg = 'WhatsApp Business API integration is not properly configured. Missing phone ID. Please complete the integration in Settings → Integrations → WhatsApp Business API.';
            Logger.error(errorMsg, {
              hasPhoneId: Boolean(actualPhoneId),
              phoneId: actualPhoneId || '(undefined)',
              newSlicePhoneId: phoneId,
              oldReduxPhoneId: oldReduxPhoneId,
              actualIsIntegrated,
              integration: whatsappIntegration || oldReduxWhatsappIntegration
            });
            throw new Error(errorMsg);
          }

          const waPayload = {
            phone: lead.phone,
            type: 'template',
            phoneId: actualPhoneId,  // 🔥 FIXED: Use actualPhoneId from whichever source has it
            template: selectedTemplate  // Template ID
          };

          Logger.log(`Sending WhatsApp template to ${lead.phone}`, waPayload);

          const response = await sendMessageToLead(waPayload);

          if (response?.data?.status || response?.status === 200) {
            Logger.log(`WhatsApp template sent successfully to ${lead.phone}`);
          } else {
            throw new Error('WhatsApp API send response invalid');
          }
        } catch (waError) {
          console.error('Error sending WhatsApp template:', waError);
          throw new Error(`Failed to send WhatsApp template to ${lead.phone}`);
        }
      } else if (selectedContentType === 'email' && lead.email) {
        // Send via Email API (Gmail or Brevo based on selected sender)
        try {
          if (!selectedSender) {
            throw new Error('Please select an email sender');
          }

          // 🔥 DEBUG: Log template structure to find subject
          Logger.log('📧 Email Template Structure:', {
            templateId: template._id,
            templateName: template.name,
            templateType: template.type,
            details: template.details,
            detailsSubject: template.details?.subject,
            detailsTitle: template.details?.title,
            detailsMessage: template.details?.message,
            fullTemplate: template
          });

          const subject = template.details?.subject || template.details?.title || 'Message from us';

          Logger.log('📧 Email payload preparation:', {
            subject,
            subjectSource: template.details?.subject ? 'details.subject' : template.details?.title ? 'details.title' : 'fallback',
            messageContentLength: messageContent?.length || 0,
            messageContentPreview: messageContent?.substring(0, 100) || '(empty)',
            sender: selectedSender.service
          });

          // Warn if message content is empty
          if (!messageContent || messageContent.trim().length === 0) {
            Logger.warn('📧 Warning: Email message content is empty!', {
              templateDetails: template.details,
              templateType: template.type
            });
          }

          if (selectedSender.service === 'brevo') {
            // Send via Brevo
            const brevoPayload = {
              sender: {
                name: selectedSender.name,
                email: selectedSender.email
              },
              to: [{
                name: lead.name || '',
                email: lead.email
              }],
              subject: subject,
              htmlContent: messageContent
            };

            Logger.log(`Sending Brevo email to ${lead.email}`, brevoPayload);
            const response = await sendEmailByBrevo(brevoPayload);

            if (response?.data?.success || response?.status === 200 || response?.data?.messageId) {
              Logger.log(`Brevo email sent successfully to ${lead.email}`);
            } else {
              throw new Error('Brevo email send response invalid');
            }
          } else if (selectedSender.service === 'zeptomail') {
            // Send via ZeptoMail
            const zeptomailPayload = {
              sender: {
                name: selectedSender.name,
                email: selectedSender.email
              },
              to: [{
                name: lead.name || '',
                email: lead.email
              }],
              subject: subject,
              message: messageContent
            };

            Logger.log('📧 [ZeptoMail] Sending email:', {
              to: lead.email,
              toName: lead.name,
              subject: subject,
              senderEmail: selectedSender.email,
              senderName: selectedSender.name,
              messageLength: messageContent?.length || 0,
              messagePreview: messageContent?.substring(0, 150) || '(empty)'
            });

            const response = await sendEmailByZeptomail(zeptomailPayload);

            Logger.log('📧 [ZeptoMail] Response:', response?.data);

            if (response?.data?.success || response?.status === 200 || response?.data?.messageId || response?.data?.data) {
              Logger.log(`📧 [ZeptoMail] Email sent successfully to ${lead.email}`);
            } else {
              Logger.error('📧 [ZeptoMail] Invalid response:', response);
              throw new Error('ZeptoMail email send response invalid');
            }
          } else {
            // Send via Gmail (default)
            const gmailPayload = {
              to: lead.email,
              subject: subject,
              message: messageContent,
              contentId: template._id,
              leadId: lead._id
            };

            Logger.log(`Sending Gmail to ${lead.email}`, gmailPayload);
            const response = await sendTestEmail(gmailPayload);

            if (response?.data?.success || response?.status === 200) {
              Logger.log(`Gmail sent successfully to ${lead.email}`);
            } else {
              throw new Error('Gmail send response invalid');
            }
          }
        } catch (emailError) {
          console.error('Error sending email:', emailError);
          throw new Error(`Failed to send email to ${lead.email}`);
        }
      }

      // Mark as completed
      setCompletedLeads(prev => ({
        ...prev,
        [leadId]: true
      }));

      // Scroll to next unprocessed lead
      setTimeout(() => {
        if (listRef.current) {
          const items = listRef.current.querySelectorAll('[data-lead-id]');
          const nextUnprocessedIndex = Array.from(items).findIndex(
            (el) => !completedLeads[(el as HTMLElement).dataset.leadId as string] &&
            (el as HTMLElement).dataset.leadId !== leadId
          );

          if (nextUnprocessedIndex !== -1) {
            items[nextUnprocessedIndex].scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }
      }, 300);
    } catch (error) {
      Logger.error('Error sending:', error);
      setCompletedLeads(prev => ({
        ...prev,
        [lead._id]: 'error'
      }));
    }
  };

  return (
    <PopupComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Send Bulk Messages"
      size="xl"
    >
      <div style={{ display: 'flex', gap: '20px', padding: '0 20px', minHeight: '500px' }}>
        {/* LEFT PANEL - Configuration (wider for better UX) */}
        <div style={{ flex: '1.8', minWidth: '400px', maxWidth: '550px' }}>
        {/* STEP 1: Select Content Type - CHIPS */}
        <FormGroup>
          <label>Select How to Send</label>
          <ChipContainer>
            {/* WhatsApp API - Only for single lead AND if integration is complete */}
            {selectedLeads.length === 1 && hasValidWhatsAppAPI && (
              <Chip
                active={selectedContentType === 'whatsapp_api'}
                onClick={() => {
                  setSelectedContentType('whatsapp_api');
                  setSelectedTemplate('');
                  setTemplateSearchQuery('');
                  setSelectedSender(null);
                }}
              >
                <MessageCircle />
                WhatsApp API
              </Chip>
            )}

            {/* Email - Only for single lead */}
            {selectedLeads.length === 1 && (
              <Chip
                active={selectedContentType === 'email'}
                onClick={() => {
                  setSelectedContentType('email');
                  setSelectedTemplate('');
                  setTemplateSearchQuery('');
                }}
              >
                <Mail />
                Email
              </Chip>
            )}
            <Chip
              active={selectedContentType === 'message'}
              onClick={() => {
                setSelectedContentType('message');
                setSelectedTemplate('');
                setTemplateSearchQuery('');
                setSelectedSender(null);
              }}
            >
              <MessageCircle />
              Message
            </Chip>
            <Chip
              active={selectedContentType === 'file'}
              onClick={() => {
                setSelectedContentType('file');
                setSelectedTemplate('');
                setTemplateSearchQuery('');
                setSelectedSender(null);
              }}
            >
              <FileText />
              File
            </Chip>
            <Chip
              active={selectedContentType === 'page'}
              onClick={() => {
                setSelectedContentType('page');
                setSelectedTemplate('');
                setTemplateSearchQuery('');
                setSelectedSender(null);
              }}
            >
              <LinkIcon />
              Page
            </Chip>
          </ChipContainer>
        </FormGroup>

        {/* Info message for bulk operations */}
        {selectedLeads.length > 1 && (
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '6px',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'start',
            gap: '8px',
            marginBottom: '16px',
            fontSize: '12px',
            color: '#0369a1'
          }}>
            <div style={{ marginTop: '2px' }}>ℹ️</div>
            <div>
              <strong>Bulk Mode:</strong> WhatsApp API and Email are only available for single lead.
              For bulk operations, use Message (opens WhatsApp Web) or share Files/Pages.
            </div>
          </div>
        )}

        {/* Warning message for missing WhatsApp integration */}
        {selectedLeads.length === 1 && !hasValidWhatsAppAPI && (
          <div style={{
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: '6px',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'start',
            gap: '8px',
            marginBottom: '16px',
            fontSize: '12px',
            color: '#c2410c'
          }}>
            <div style={{ marginTop: '2px' }}>⚠️</div>
            <div>
              <strong>WhatsApp API Not Setup:</strong> To use WhatsApp Business API for automated messaging,
              complete the integration in Settings → Integrations → WhatsApp Business API.
            </div>
          </div>
        )}

        {/* Email Sender Selection - Compact Dropdown */}
        {selectedContentType === 'email' && (
          <FormGroup>
            <label>Select Sender</label>
            {emailSenders.length > 0 ? (
              <div ref={senderDropdownRef} style={{ position: 'relative' }}>
                {/* Selected Sender Display / Trigger Button */}
                <div
                  onClick={() => setShowSenderDropdown(!showSenderDropdown)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    border: `1px solid ${selectedSender ? '#3faefd' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: selectedSender ? '#f0f9ff' : 'white',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {selectedSender ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: selectedSender.service === 'zeptomail' ? '#fef3c7' :
                                   selectedSender.service === 'brevo' ? '#dbeafe' : '#dcfce7',
                        color: selectedSender.service === 'zeptomail' ? '#92400e' :
                               selectedSender.service === 'brevo' ? '#1e40af' : '#166534',
                        textTransform: 'uppercase',
                        flexShrink: 0
                      }}>
                        {selectedSender.service === 'zeptomail' ? 'Zepto' :
                         selectedSender.service === 'brevo' ? 'Brevo' : 'Gmail'}
                      </span>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#1f2937' }}>{selectedSender.name}</span>
                        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>({selectedSender.email})</span>
                      </div>
                    </div>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>Click to select a sender...</span>
                  )}
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    transform: showSenderDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}>▼</span>
                </div>

                {/* Dropdown Menu */}
                {showSenderDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    background: 'white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 100,
                    maxHeight: '250px',
                    overflowY: 'auto'
                  }}>
                    {/* Gmail Senders */}
                    {emailSenders.filter(s => s.service === 'gmail').length > 0 && (
                      <>
                        <div style={{
                          padding: '6px 10px',
                          background: '#f3f4f6',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#166534',
                          borderBottom: '1px solid #e5e7eb',
                          position: 'sticky',
                          top: 0
                        }}>
                          📧 Gmail ({emailSenders.filter(s => s.service === 'gmail').length})
                        </div>
                        {emailSenders.filter(s => s.service === 'gmail').map((sender, idx) => (
                          <div
                            key={`gmail-${idx}`}
                            onClick={() => {
                              setSelectedSender(sender);
                              setShowSenderDropdown(false);
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              background: selectedSender?.email === sender.email && selectedSender?.service === 'gmail' ? '#dcfce7' : 'white',
                              borderLeft: selectedSender?.email === sender.email && selectedSender?.service === 'gmail' ? '3px solid #22c55e' : '3px solid transparent',
                              fontSize: '13px',
                              transition: 'background 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!(selectedSender?.email === sender.email && selectedSender?.service === 'gmail')) {
                                e.currentTarget.style.background = '#f0fdf4';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!(selectedSender?.email === sender.email && selectedSender?.service === 'gmail')) {
                                e.currentTarget.style.background = 'white';
                              }
                            }}
                          >
                            <div style={{ fontWeight: 500, color: '#1f2937' }}>{sender.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{sender.email}</div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Brevo Senders */}
                    {emailSenders.filter(s => s.service === 'brevo').length > 0 && (
                      <>
                        <div style={{
                          padding: '6px 10px',
                          background: '#f3f4f6',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#1e40af',
                          borderBottom: '1px solid #e5e7eb',
                          borderTop: '1px solid #e5e7eb',
                          position: 'sticky',
                          top: 0
                        }}>
                          📨 Brevo ({emailSenders.filter(s => s.service === 'brevo').length})
                        </div>
                        {emailSenders.filter(s => s.service === 'brevo').map((sender, idx) => (
                          <div
                            key={`brevo-${idx}`}
                            onClick={() => {
                              setSelectedSender(sender);
                              setShowSenderDropdown(false);
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              background: selectedSender?.email === sender.email && selectedSender?.service === 'brevo' ? '#dbeafe' : 'white',
                              borderLeft: selectedSender?.email === sender.email && selectedSender?.service === 'brevo' ? '3px solid #3b82f6' : '3px solid transparent',
                              fontSize: '13px',
                              transition: 'background 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!(selectedSender?.email === sender.email && selectedSender?.service === 'brevo')) {
                                e.currentTarget.style.background = '#eff6ff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!(selectedSender?.email === sender.email && selectedSender?.service === 'brevo')) {
                                e.currentTarget.style.background = 'white';
                              }
                            }}
                          >
                            <div style={{ fontWeight: 500, color: '#1f2937' }}>{sender.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{sender.email}</div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* ZeptoMail Senders */}
                    {emailSenders.filter(s => s.service === 'zeptomail').length > 0 && (
                      <>
                        <div style={{
                          padding: '6px 10px',
                          background: '#f3f4f6',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#92400e',
                          borderBottom: '1px solid #e5e7eb',
                          borderTop: '1px solid #e5e7eb',
                          position: 'sticky',
                          top: 0
                        }}>
                          ⚡ ZeptoMail ({emailSenders.filter(s => s.service === 'zeptomail').length})
                        </div>
                        {emailSenders.filter(s => s.service === 'zeptomail').map((sender, idx) => (
                          <div
                            key={`zeptomail-${idx}`}
                            onClick={() => {
                              setSelectedSender(sender);
                              setShowSenderDropdown(false);
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              background: selectedSender?.email === sender.email && selectedSender?.service === 'zeptomail' ? '#fef3c7' : 'white',
                              borderLeft: selectedSender?.email === sender.email && selectedSender?.service === 'zeptomail' ? '3px solid #f59e0b' : '3px solid transparent',
                              fontSize: '13px',
                              transition: 'background 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!(selectedSender?.email === sender.email && selectedSender?.service === 'zeptomail')) {
                                e.currentTarget.style.background = '#fffbeb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!(selectedSender?.email === sender.email && selectedSender?.service === 'zeptomail')) {
                                e.currentTarget.style.background = 'white';
                              }
                            }}
                          >
                            <div style={{ fontWeight: 500, color: '#1f2937' }}>{sender.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{sender.email}</div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                padding: '12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#dc2626',
                fontSize: '13px'
              }}>
                No email senders configured. Add senders in Settings → Integrations.
              </div>
            )}
          </FormGroup>
        )}

        {/* Template section - always shown since Message is selected by default */}
        <>
            {/* STEP 2: Select Template with Search */}
            {loadingWaTemplates && selectedContentType === 'whatsapp_api' ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '13px'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #3faefd',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite',
                  margin: '0 auto 8px'
                }} />
                Loading WhatsApp templates...
              </div>
            ) : filteredTemplates.length > 0 ? (
              <FormGroup>
                <label>Select Template</label>
                <SearchInputContainer>
                  <SearchIcon>
                    <Search />
                  </SearchIcon>
                  <SearchInput
                    placeholder="Search templates..."
                    value={templateSearchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateSearchQuery(e.target.value)}
                  />
                </SearchInputContainer>

                {/* Template dropdown - always visible, no layout shift */}
                <TemplateDropdown>
                  {searchedTemplates.length > 0 ? (
                    searchedTemplates.map((template) => {
                      // Handle different template types
                      let preview = '';
                      let title = '';

                      if (template.type === 'whatsapp_template') {
                        // WhatsApp template structure
                        const bodyComponent = template.components?.find((c: any) => c.type === 'BODY');
                        preview = bodyComponent?.text || 'No body content';
                        title = template.name || 'Unnamed';
                      } else if (template.type === 'email_template') {
                        // 🔥 Email template structure - show subject as preview
                        const subject = template.details?.subject || template.details?.title || 'No subject';
                        preview = `📧 ${subject}`;
                        title = template.details?.title || template.name || 'Unnamed';
                      } else {
                        // Other content templates (message, file, page)
                        preview = template.details?.message || template.details?.description || 'No content';
                        title = template.details?.title || template.name || 'Unnamed';
                      }

                      const previewText = preview.substring(0, 120).replace(/@lead name/g, 'Customer');

                      return (
                        <TemplateOption
                          key={template._id}
                          selected={selectedTemplate === template._id}
                          onClick={() => setSelectedTemplate(template._id)}
                        >
                          <div className="template-title">
                            {title}
                            {template.type === 'whatsapp_template' && (
                              <span style={{
                                marginLeft: '8px',
                                fontSize: '10px',
                                background: '#25d366',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px'
                              }}>
                                WA
                              </span>
                            )}
                          </div>
                          <div className="template-preview">
                            {previewText}...
                          </div>
                          <TemplateTooltip className="template-tooltip">
                            {preview}
                          </TemplateTooltip>
                        </TemplateOption>
                      );
                    })
                  ) : (
                    <div style={{
                      padding: '12px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '13px'
                    }}>
                      No templates match your search
                    </div>
                  )}
                </TemplateDropdown>
              </FormGroup>
            ) : (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                padding: '12px',
                display: 'flex',
                gap: '8px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '13px'
              }}>
                <XCircle size={16} />
                <span>No templates available for this content type</span>
              </div>
            )}

            {/* STEP 3: Template Preview */}
            {templatePreview && (
              <FormGroup>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label style={{ margin: 0 }}>Preview</label>
                  <Button
                    onClick={() => setShowPreview(!showPreview)}
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                  >
                    <Eye size={14} />
                    {showPreview ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {showPreview && (
                  <PreviewBox>{templatePreview}</PreviewBox>
                )}
              </FormGroup>
            )}

            {/* Helper message when something is missing */}
            {(!selectedTemplate && filteredTemplates.length > 0) || (selectedContentType === 'email' && !selectedSender && emailSenders.length > 0) ? (
              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                color: '#1d4ed8',
                fontSize: '13px'
              }}>
                <MessageCircle size={16} />
                <span>
                  {!selectedTemplate && filteredTemplates.length > 0
                    ? 'Please select a template to enable sending'
                    : 'Please select an email sender to enable sending'
                  }
                </span>
              </div>
            ) : null}
        </>
        </div>

        {/* RIGHT PANEL - Leads List (narrower, just shows lead names) */}
        <div style={{ flex: '1', minWidth: '280px', display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e5e7eb', paddingLeft: '20px' }}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontWeight: 600, fontSize: '14px', color: '#374151' }}>
                Recipients ({validLeads.length} leads)
              </label>
            </div>

            {/* Stats */}
            <StatsContainer>
              <StatCard>
                <div className="stat-label">Total Leads</div>
                <div className="stat-value">{totalLeads}</div>
              </StatCard>
              <StatCard>
                <div className="stat-label">Sent</div>
                <div className="stat-value">{completedCount}</div>
              </StatCard>
              <StatCard>
                <div className="stat-label">Pending</div>
                <div className="stat-value">{totalLeads - completedCount}</div>
              </StatCard>
            </StatsContainer>

            {/* Warning for filtered leads */}
            {selectedLeads.length > validLeads.length && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                padding: '12px',
                display: 'flex',
                gap: '8px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '13px'
              }}>
                <XCircle size={16} />
                <span>
                  {selectedLeads.length - validLeads.length} leads are not compatible with {selectedContentType}.
                </span>
              </div>
            )}

            {/* Leads List - Full height */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }} ref={listRef}>
              {validLeads.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '32px 16px',
                  color: '#9ca3af',
                  fontSize: '13px'
                }}>
                  No valid leads for {selectedContentType}
                </div>
              ) : (
                validLeads.map((lead) => {
                  // 🔥 Get lead status with defensive checks
                  const leadStatus = completedLeads[lead._id];
                  const isError = leadStatus === 'error';
                  const isLoading = leadStatus === 'loading';
                  const isCompleted = leadStatus === true;
                  const isPending = !leadStatus; // undefined or falsy

                  return (
                  <LeadItemContainer
                    key={lead._id}
                    data-lead-id={lead._id}
                    className={isCompleted ? 'completed' : isError ? 'error' : ''}
                  >
                    <LeadInfo>
                      <div className="lead-name">{lead.name || 'Unnamed Lead'}</div>
                      <div className="lead-contact">
                        {selectedContentType === 'whatsapp_api' ? lead.phone : selectedContentType === 'email' ? lead.email : lead.phone || lead.email}
                      </div>
                    </LeadInfo>
                    <StatusBadge
                      status={
                        isCompleted ? 'completed' :
                        isLoading ? 'loading' :
                        isError ? 'error' : 'pending'
                      }
                    >
                      {isCompleted ? (
                        <>
                          <Check size={14} /> Sent
                        </>
                      ) : isLoading ? (
                        <>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid #0284c7',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.6s linear infinite'
                          }} />
                          Sending...
                        </>
                      ) : isError ? (
                        <>
                          <XCircle size={14} /> Failed
                        </>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>Pending</span>
                      )}
                    </StatusBadge>
                    {/* Copy Message Button - Only for message/file/page types (not API-based sends) */}
                    {(selectedContentType === 'message' || selectedContentType === 'file' || selectedContentType === 'page') && (
                      <Button
                        onClick={() => handleCopyMessage(lead)}
                        disabled={!selectedTemplate}
                        style={{ marginLeft: '8px', padding: '8px 10px' }}
                        title={
                          !selectedTemplate
                            ? 'Please select a template first'
                            : copiedLeadId === lead._id
                              ? 'Copied!'
                              : `Copy message for ${lead.name || 'this lead'}`
                        }
                      >
                        <Copy size={14} />
                        {copiedLeadId === lead._id ? 'Copied!' : 'Copy'}
                      </Button>
                    )}
                    <Button
                      onClick={() => handleSendToLead(lead)}
                      primary={isPending || isError}
                      danger={isError}
                      disabled={
                        !selectedTemplate ||
                        (selectedContentType === 'email' && !selectedSender) ||
                        isCompleted ||
                        isLoading
                      }
                      style={{ marginLeft: '8px' }}
                      title={
                        !selectedTemplate
                          ? 'Please select a template first'
                          : (selectedContentType === 'email' && !selectedSender)
                            ? 'Please select an email sender'
                            : isCompleted
                              ? 'Already sent'
                              : isLoading
                                ? 'Sending in progress...'
                                : isError
                                  ? 'Previous attempt failed - retry'
                                  : `Send to ${lead.name || 'this lead'}`
                      }
                    >
                      <MessageCircle size={14} />
                      {/* 🔥 FIXED: Only show "Retry" if explicitly in error state */}
                      {isError ? 'Retry' : 'Send'}
                    </Button>
                  </LeadItemContainer>
                  );
                })
              )}
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
        </div>
      </div>
    </PopupComponent>
  );
};

export default BulkShareModal;